import type { Networkish } from "@/types";
import React from "react";

import { ImageWithFallback } from "@/components/Rewards/ImageWithFallback";
import useChainInfo from "@/hooks/useChainInfo";
import { SupLoader } from "@repo/core-ui";
import { classNames } from "@/utils";
import { SupCoinAlt } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface UiTagProps {
  asset: {
    logoURI?: string;
    icon?: string;
    name: string;
    symbol: string;
  };
  chainId?: Networkish;
  hasBorder?: boolean;
  isSmall?: boolean;
  isPinnedSmall?: boolean;
  className?: string | boolean;
}

export default function UiTag(props: UiTagProps) {
  const assetUrl = props.asset.logoURI || props.asset.icon;
  const { data: chainInfo, isLoading } = useChainInfo(props.chainId);

  return (
    <div
      className={classNames(
        "flex w-fit items-center space-x-[4px] p-[3px] pr-[8px]",
        props.hasBorder && "rounded-[384px] border-neutral-600",
        props.hasBorder && (props.isPinnedSmall ? "border" : "border-[0.5px]"),
        props.isSmall ? "h-[32px]" : props.isPinnedSmall ? "h-[30px]" : "h-[42px]",
        props.className
      )}
    >
      <div className={classNames("relative flex items-center justify-center", props.isSmall ? "h-[32px] w-[32px]" : props.isPinnedSmall ? "h-[30px] w-auto" : "h-[40px] w-[40px]")}>
        {assetUrl ? (
          <ImageWithFallback
            className={classNames("w-auto rounded-full object-cover object-center", props.isSmall ? "h-[20px]" : props.isPinnedSmall ? "h-[24px]" : "h-[32px]")}
            height={240}
            alt={props.asset.name}
            width={240}
            srcSet={assetUrl}
            fallbackSrc="/assets/img/placeholder/token.svg"
          />
        ) : (
          <SupCoinAlt className={classNames("w-auto rounded-full object-cover object-center", props.isSmall ? "h-[20px]" : props.isPinnedSmall ? "h-[24px]" : "h-[32px]")} />
        )}
        {chainInfo && (
          <span
            className={classNames(
              "absolute right-0 flex items-center justify-center overflow-hidden rounded-full bg-neutral",
              props.isSmall || props.isPinnedSmall ? "bottom-[2px] m-[1px] h-[12px] w-[12px]" : "bottom-0 m-[2px] h-[16px] w-[16px]"
            )}
          >
            {isLoading ? <SupLoader isSmall /> : chainInfo.icon && <img src={chainInfo.icon} alt={chainInfo.name} className="h-full w-auto object-cover object-center" />}
          </span>
        )}
      </div>
      <div
        className={classNames(
          "flex h-full flex-col items-start justify-center font-mono uppercase tracking-[.5px] text-neutral-900",
          props.isSmall || props.isPinnedSmall ? "leading-none" : "leading-[18px]"
        )}
      >
        <span className={classNames(props.isSmall || props.isPinnedSmall ? "text-nowrap text-[12px]" : "text-[16px]")}>{props.asset.symbol}</span>
        {isLoading ? (
          <span className={twMerge("text-gray-700", props.isSmall ? "text-[10px]" : "text-[12px]")}>Loading...</span>
        ) : (
          chainInfo?.name && <span className={twMerge(props.isSmall ? "text-[10px]" : "text-[12px]")}>{chainInfo.name}</span>
        )}
      </div>
    </div>
  );
}
